/**
 * NLP Analytics Utilities
 * Text processing, word clouds, keyword extraction, topic analysis
 */

// Common English stopwords to filter out
const STOPWORDS = new Set([
  'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i',
  'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at',
  'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she',
  'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their',
  'what', 'so', 'up', 'out', 'if', 'about', 'who', 'get', 'which', 'go',
  'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know',
  'take', 'people', 'into', 'year', 'your', 'good', 'some', 'could', 'them',
  'see', 'other', 'than', 'then', 'now', 'look', 'only', 'come', 'its',
  'over', 'think', 'also', 'back', 'after', 'use', 'two', 'how', 'our',
  'work', 'first', 'well', 'way', 'even', 'new', 'want', 'because', 'any',
  'these', 'give', 'day', 'most', 'us', 'is', 'was', 'are', 'been', 'has',
  'had', 'were', 'said', 'did', 'having', 'may', 'such', 'being', 'through',
  // Business/tech common words to filter
  'company', 'companies', 'business', 'platform', 'service', 'product',
  'technology', 'technologies', 'provides', 'provide', 'providing', 'offer',
  'offers', 'offering', 'based', 'inc', 'llc', 'ltd', 'corp', 'corporation'
]);

/**
 * Tokenize and clean text
 * @param {String} text - Raw text to process
 * @returns {Array} Array of cleaned tokens
 */
const tokenize = (text) => {
  if (!text) return [];

  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ') // Keep hyphens for compound words
    .split(/\s+/)
    .filter(word =>
      word.length > 2 && // Min 3 characters
      !STOPWORDS.has(word) &&
      !/^\d+$/.test(word) // Filter pure numbers
    );
};

/**
 * Calculate word frequencies
 * @param {Array} tokens - Array of word tokens
 * @returns {Object} Word frequency map
 */
const calculateWordFrequency = (tokens) => {
  const frequency = {};

  tokens.forEach(word => {
    frequency[word] = (frequency[word] || 0) + 1;
  });

  return frequency;
};

/**
 * Generate word cloud data from text array
 * @param {Array} textArray - Array of text strings
 * @param {Number} topN - Number of top words to return
 * @returns {Array} Array of {text, value} objects for word cloud
 */
export const generateWordCloudData = (textArray, topN = 100) => {
  if (!Array.isArray(textArray)) {
    return [];
  }

  const allTokens = [];

  textArray.forEach(text => {
    if (text && typeof text === 'string') {
      allTokens.push(...tokenize(text));
    }
  });

  if (allTokens.length === 0) {
    return [];
  }

  const frequency = calculateWordFrequency(allTokens);

  // Convert to array and sort by frequency
  const wordCloudData = Object.entries(frequency)
    .map(([text, value]) => ({ text, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, topN);

  return wordCloudData;
};

/**
 * Extract keywords using TF-IDF approach (simplified)
 * @param {Array} documents - Array of text documents
 * @param {Number} topN - Number of keywords per document
 * @returns {Array} Array of keyword sets
 */
export const extractKeywords = (documents, topN = 5) => {
  // Calculate term frequency for each document
  const documentTerms = documents.map(doc => {
    const tokens = tokenize(doc);
    return calculateWordFrequency(tokens);
  });

  // Calculate document frequency (how many docs contain each term)
  const documentFrequency = {};
  documentTerms.forEach(termFreq => {
    Object.keys(termFreq).forEach(term => {
      documentFrequency[term] = (documentFrequency[term] || 0) + 1;
    });
  });

  const totalDocs = documents.length;

  // Calculate TF-IDF for each document
  const keywords = documentTerms.map((termFreq, docIdx) => {
    const tfidf = {};

    Object.entries(termFreq).forEach(([term, freq]) => {
      const tf = freq / Object.values(termFreq).reduce((a, b) => a + b, 0);
      const idf = Math.log(totalDocs / (documentFrequency[term] || 1));
      tfidf[term] = tf * idf;
    });

    // Get top N keywords
    return Object.entries(tfidf)
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([term]) => term);
  });

  return keywords;
};

/**
 * Calculate detailed TF-IDF scores for documents
 * Returns top terms with their scores and document frequencies
 * @param {Array} documents - Array of text documents
 * @param {Number} topN - Number of top terms to return
 * @returns {Object} Detailed TF-IDF analysis
 */
export const calculateDetailedTFIDF = (documents, topN = 20) => {
  if (!Array.isArray(documents) || documents.length === 0) {
    return { terms: [], summary: {} };
  }

  // Tokenize all documents
  const documentTerms = documents.map(doc => {
    if (!doc || typeof doc !== 'string') return {};
    const tokens = tokenize(doc);
    return calculateWordFrequency(tokens);
  });

  // Calculate document frequency (DF) for each term
  const documentFrequency = {};
  const totalTermFrequency = {};

  documentTerms.forEach(termFreq => {
    if (!termFreq || typeof termFreq !== 'object') return;
    Object.keys(termFreq).forEach(term => {
      documentFrequency[term] = (documentFrequency[term] || 0) + 1;
      totalTermFrequency[term] = (totalTermFrequency[term] || 0) + termFreq[term];
    });
  });

  const totalDocs = documents.length;

  // Calculate average TF-IDF for each term across all documents
  const termTFIDF = {};

  documentTerms.forEach((termFreq) => {
    if (!termFreq || typeof termFreq !== 'object') return;

    const values = Object.values(termFreq);
    if (values.length === 0) return; // Skip empty documents

    const totalWords = values.reduce((a, b) => a + b, 0);
    if (totalWords === 0) return; // Avoid division by zero

    Object.entries(termFreq).forEach(([term, freq]) => {
      const tf = freq / totalWords;
      const idf = Math.log(totalDocs / (documentFrequency[term] || 1));
      const tfidf = tf * idf;

      if (!termTFIDF[term]) {
        termTFIDF[term] = [];
      }

      if (!Array.isArray(termTFIDF[term])) {
        console.error('🔴 termTFIDF[term] is not an array!', term, termTFIDF[term]);
        termTFIDF[term] = [];
      }

      termTFIDF[term].push(tfidf);
    });
  });

  // Average TF-IDF and add metadata
  const results = Object.entries(termTFIDF).map(([term, scores]) => {
    const avgTFIDF = scores.reduce((a, b) => a + b, 0) / scores.length;
    const df = documentFrequency[term];
    const idf = Math.log(totalDocs / df);
    const totalOccurrences = totalTermFrequency[term];

    return {
      term,
      tfidf: avgTFIDF,
      idf,
      documentFrequency: df,
      documentPercentage: ((df / totalDocs) * 100).toFixed(1),
      totalOccurrences,
      avgOccurrencesPerDoc: (totalOccurrences / df).toFixed(2),
      importance: avgTFIDF > 0.1 ? 'high' : avgTFIDF > 0.05 ? 'medium' : 'low'
    };
  });

  // Sort by TF-IDF score
  const topTerms = results
    .sort((a, b) => b.tfidf - a.tfidf)
    .slice(0, topN);

  // Summary statistics
  const summary = {
    totalDocuments: totalDocs,
    totalUniqueTerms: Object.keys(termTFIDF).length,
    avgTermsPerDocument: (Object.values(totalTermFrequency).reduce((a, b) => a + b, 0) / totalDocs).toFixed(1),
    topTermAvgTFIDF: topTerms.length > 0 ? topTerms[0].tfidf.toFixed(4) : 0,
    mostFrequentTerm: topTerms.length > 0 ? topTerms[0].term : 'N/A'
  };

  return {
    terms: topTerms,
    summary
  };
};

/**
 * Analyze keyword trends over time
 * @param {Array} data - Array of {text, date} objects
 * @param {Array} keywords - Keywords to track
 * @returns {Array} Time series data for each keyword
 */
export const analyzeKeywordTrends = (data, keywords = []) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  // Auto-extract top keywords if not provided
  if (!Array.isArray(keywords) || keywords.length === 0) {
    const allText = data.map(d => d?.text).filter(Boolean);
    const wordCloud = generateWordCloudData(allText, 10);
    keywords = wordCloud.map(w => w.text);
  }

  // Group by year-month
  const timeGroups = {};

  data.forEach(item => {
    if (!item.date || !item.text) return;

    const date = new Date(item.date);
    if (isNaN(date.getTime())) return;

    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    if (!timeGroups[yearMonth]) {
      timeGroups[yearMonth] = { date: yearMonth, texts: [] };
    }

    timeGroups[yearMonth].texts.push(item.text);
  });

  // Calculate keyword frequencies per time period
  const trends = Object.entries(timeGroups)
    .map(([date, group]) => {
      const allTokens = [];
      group.texts.forEach(text => {
        allTokens.push(...tokenize(text));
      });

      const freq = calculateWordFrequency(allTokens);
      const totalWords = allTokens.length;

      const point = { date };
      keywords.forEach(keyword => {
        point[keyword] = freq[keyword] ? (freq[keyword] / totalWords * 1000) : 0; // Per 1000 words
      });

      return point;
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  return { trends, keywords };
};

/**
 * Group text by category and analyze
 * @param {Array} data - Array of {text, category} objects
 * @param {Number} topN - Top words per category
 * @returns {Object} Category analysis
 */
export const analyzeByCategorya = (data, topN = 20) => {
  const categories = {};

  data.forEach(item => {
    if (!item.category || !item.text) return;

    if (!categories[item.category]) {
      categories[item.category] = [];
    }

    categories[item.category].push(item.text);
  });

  const analysis = {};

  Object.entries(categories).forEach(([category, texts]) => {
    const wordCloud = generateWordCloudData(texts, topN);
    analysis[category] = {
      count: texts.length,
      topWords: wordCloud
    };
  });

  return analysis;
};

/**
 * Calculate NLP analytics for deals data
 * @param {Array} deals - Array of deal objects
 * @param {Object} options - Options {expanded}
 * @returns {Object} NLP analytics results
 */
export const calculateDealsNLP = (deals, options = {}) => {
  if (!deals || deals.length === 0) {
    return {
      wordClouds: [],
      keywordTrends: { trends: [], keywords: [] },
      topicDistribution: [],
      sourceTexts: []
    };
  }

  const results = {
    wordClouds: [],
    keywordTrends: { trends: [], keywords: [] },
    topicDistribution: [],
    sourceTexts: []
  };

  // 1. Deal Synopsis Word Cloud
  const dealTexts = deals
    .map(d => d.deal_synopsis || d.description)
    .filter(Boolean);

  // Store source texts for Python NLP
  results.sourceTexts = dealTexts;

  if (dealTexts.length > 0) {
    const dealWordCloud = generateWordCloudData(dealTexts, 100);
    results.wordClouds.push({
      id: 'deal_synopsis_cloud',
      title: 'Deal Synopsis Keywords',
      data: dealWordCloud,
      note: `${dealTexts.length} deals analyzed • Top 100 terms`
    });
  }

  // 2. Verticals/Industries Word Cloud
  const verticals = [];
  deals.forEach(d => {
    if (d.verticals && Array.isArray(d.verticals)) {
      verticals.push(...d.verticals);
    } else if (d.primary_industry) {
      verticals.push(d.primary_industry);
    }
  });

  if (verticals.length > 0) {
    const verticalFreq = calculateWordFrequency(
      verticals.map(v => v.toLowerCase())
    );
    const verticalCloud = Object.entries(verticalFreq)
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);

    results.wordClouds.push({
      id: 'verticals_cloud',
      title: 'Industry & Vertical Distribution',
      data: verticalCloud,
      note: `${verticals.length} vertical tags • Top 50 industries`
    });
  }

  // 3. Keywords Word Cloud (if available)
  const keywords = [];
  deals.forEach(d => {
    if (d.keywords && Array.isArray(d.keywords)) {
      keywords.push(...d.keywords);
    } else if (typeof d.keywords === 'string') {
      keywords.push(...d.keywords.split(',').map(k => k.trim()));
    }
  });

  if (keywords.length > 0) {
    const keywordFreq = calculateWordFrequency(
      keywords.map(k => k.toLowerCase())
    );
    const keywordCloud = Object.entries(keywordFreq)
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 80);

    results.wordClouds.push({
      id: 'keywords_cloud',
      title: 'Deal Keywords & Tags',
      data: keywordCloud,
      note: `${keywords.length} keyword tags • Top 80 terms`
    });
  }

  // 4. Keyword Trends Over Time (if dates available)
  const dealsWithDates = deals
    .filter(d => d.deal_date && (d.deal_synopsis || d.description))
    .map(d => ({
      date: d.deal_date,
      text: d.deal_synopsis || d.description
    }));

  if (dealsWithDates.length > 10) {
    const trendData = analyzeKeywordTrends(dealsWithDates, []);
    results.keywordTrends = trendData;
  }

  // 5. Topic Distribution by Deal Type
  const dealTypes = {};
  deals.forEach(d => {
    const type = d.deal_type || 'Unknown';
    if (!dealTypes[type]) {
      dealTypes[type] = 0;
    }
    dealTypes[type]++;
  });

  results.topicDistribution = Object.entries(dealTypes)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  // 6. Detailed TF-IDF Analysis
  const allDescriptions = deals.map(d => d.deal_synopsis).filter(Boolean);
  if (allDescriptions.length > 0) {
    results.tfidfAnalysis = calculateDetailedTFIDF(allDescriptions, 20);
  } else {
    results.tfidfAnalysis = { terms: [], summary: {} };
  }

  return results;
};

/**
 * Calculate NLP analytics for companies data
 * @param {Array} companies - Array of company objects
 * @returns {Object} NLP analytics results
 */
export const calculateCompaniesNLP = (companies) => {
  if (!companies || companies.length === 0) {
    return { wordClouds: [], topicDistribution: [], sourceTexts: [] };
  }

  const results = {
    wordClouds: [],
    topicDistribution: [],
    sourceTexts: []
  };

  // Company descriptions word cloud
  const descriptions = companies
    .map(c => c.description)
    .filter(Boolean);

  // Store source texts for Python NLP
  results.sourceTexts = descriptions;

  if (descriptions.length > 0) {
    const descCloud = generateWordCloudData(descriptions, 100);
    results.wordClouds.push({
      id: 'company_desc_cloud',
      title: 'Company Descriptions',
      data: descCloud,
      note: `${descriptions.length} companies • Top 100 terms`
    });
  }

  // Business status distribution
  const statuses = {};
  companies.forEach(c => {
    const status = c.business_status || 'Unknown';
    statuses[status] = (statuses[status] || 0) + 1;
  });

  results.topicDistribution = Object.entries(statuses)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Detailed TF-IDF Analysis
  const allDescriptions = companies.map(c => c.description).filter(Boolean);
  if (allDescriptions.length > 0) {
    results.tfidfAnalysis = calculateDetailedTFIDF(allDescriptions, 20);
  } else {
    results.tfidfAnalysis = { terms: [], summary: {} };
  }

  return results;
};

/**
 * Calculate NLP analytics for investors data
 * @param {Array} investors - Array of investor objects
 * @returns {Object} NLP analytics results
 */
export const calculateInvestorsNLP = (investors) => {
  if (!investors || investors.length === 0) {
    return { wordClouds: [], topicDistribution: [], sourceTexts: [] };
  }

  const results = {
    wordClouds: [],
    topicDistribution: [],
    sourceTexts: []
  };

  // Investor descriptions word cloud
  const descriptions = investors
    .map(i => i.description)
    .filter(Boolean);

  // Store source texts for Python NLP
  results.sourceTexts = descriptions;

  if (descriptions.length > 0) {
    const descCloud = generateWordCloudData(descriptions, 100);
    results.wordClouds.push({
      id: 'investor_desc_cloud',
      title: 'Investor Focus & Strategy',
      data: descCloud,
      note: `${descriptions.length} investors • Top 100 terms`
    });
  }

  // Investment focus areas (from descriptions)
  const focusTerms = ['venture', 'capital', 'equity', 'growth', 'early-stage',
                      'late-stage', 'seed', 'series', 'private', 'fund'];

  if (descriptions.length > 0) {
    const allTokens = [];
    descriptions.forEach(desc => {
      allTokens.push(...tokenize(desc));
    });

    const freq = calculateWordFrequency(allTokens);
    const focusData = focusTerms
      .filter(term => freq[term] > 0)
      .map(term => ({ name: term, value: freq[term] }))
      .sort((a, b) => b.value - a.value);

    results.topicDistribution = focusData;
  }

  // Detailed TF-IDF Analysis
  if (descriptions.length > 0) {
    results.tfidfAnalysis = calculateDetailedTFIDF(descriptions, 20);
  } else {
    results.tfidfAnalysis = { terms: [], summary: {} };
  }

  return results;
};
